/**
 * Measurement Review panel — per-endpoint quality read-out for Smoothed mode.
 *
 * Each endpoint's hemisphere neighbourhood (see nearbyPoints.ts) yields six
 * checks. Values convert from the cloud's native unit to inches (see IN_PER_UNIT)
 * for both display and grading; Plane Tilt is a unitless rise/run. Plane-based
 * checks need >= 3 neighbours or they show "—".
 *
 * The panel does NOT show a single grade. Each endpoint lists the five known
 * failure causes (each OK / Watch / Risk) under an overall Clean / Watch / Risk
 * header = the worst of the causes. The six checks are the raw signals the causes
 * are read from — shown in the endpoint header hover; a cause-row hover shows the
 * check(s) that cause is judged from. Cause → check(s):
 *   Too few points  ← Nearby Points
 *   Flying point    ← Vertical Offset + Centroid Offset
 *   Double surface  ← Residual Spread          (shares its signal with Mixed pixel)
 *   Vertical face   ← Plane Tilt
 *   Mixed pixel     ← Residual Spread + Vertical Offset
 *
 * ── Data sufficiency ────────────────────────────────────────────────────────
 * Nearby Points   Count of unique neighbours in the hemisphere.
 *                 → How trustworthy the whole estimate is. When it is low, treat
 *                   every other check with caution — a plane through 3 points fits
 *                   perfectly by construction, so its residuals look falsely good.
 *                   >10 good · 5–10 moderate · <5 poor.
 *
 * ── Surface quality: is the patch one clean surface, or a mess? ──────────────
 * Elevation Spread  Standard deviation of the neighbours' heights (z).
 *                   → How much the nearby heights vary. Simple, but it also grows
 *                     with real terrain slope, so a clean-but-steep surface reads
 *                     high (a known false-positive). <0.24 · 0.24–0.60 · >0.60 in.
 * Residual Spread   RMS of the neighbours' distance to a local least-squares plane
 *                   — i.e. Elevation Spread with the local slope removed.
 *                   → Noise / roughness only (double layer, curb face, edge),
 *                     independent of steepness. Same thresholds as Elevation Spread.
 *
 * ── Surface orientation ─────────────────────────────────────────────────────
 * Plane Tilt        sqrt(a²+b²) of the local best-fit plane (z=ax+by+c) —
 *                   = tan(angle from horizontal) = rise/run.
 *                   → Ground / ramp (single-digit) vs a near-vertical wall / curb
 *                     face. <0.5 good · 0.5–1.0 moderate · >1.0 poor.
 *
 * ── Click quality: did the drop land on the surface, or on an outlier? ───────
 * Centroid Offset   3D distance from the click to P′ (the component-wise median),
 *                   graded on the EXCESS above the hemisphere's geometric floor
 *                   (~4r/3π) — a one-sided hemisphere produces that offset even for
 *                   a perfect click. → Outlier / flying-point hint.
 *                   excess <0.36 · 0.36–1.20 · >1.20 in.
 * Vertical Offset   Vertical distance from the click to that local plane — how far
 *                   the dropped point floats above/below the surface.
 *                   → A clean flying-point check, immune to the horizontal pull.
 *                     <0.36 · 0.36–1.20 · >1.20 in.
 *
 * Reading Residual Spread + Vertical Offset together:
 *   high Vertical Offset + low Residual Spread → surface fine, click is off → re-click.
 *   high Residual Spread + low Vertical Offset → click fine, data here is messy → move / distrust.
 *   Nearby Points red → distrust all of the above until more density is loaded.
 */
import React from 'react';
import { Modal, Tooltip } from 'antd';

import { NeighborSummary, HEMISPHERE_RADIUS_FT, computeDistanceAndSlope, isNearVertical } from '../utils/nearbyPoints';

interface Props {
    visible: boolean;
    start: NeighborSummary | null;
    end: NeighborSummary | null;
    smoothedMeasure?: { distance: number; height?: number; slopePercent: number } | null;
    /** Point cloud's native length unit — values display in it, thresholds convert. */
    lengthUnit?: 'm' | 'ft';
    /** Display unit for the measured distance (e.g. inches ″), matching Potree Standard. */
    distanceUnit?: { code: string; perNativeUnit: number };
    /**
     * 'smoothed' → full panel (Standard/Smoothed/Δ + Point Quality).
     * 'standard' → Result with the Standard row only; no Smoothed/Δ, no Point Quality.
     */
    mode?: 'smoothed' | 'standard';
    /** Potree name of the measurement being shown; the tool, mode and Result
     * columns are derived from it (e.g. "DistanceAndSlopeSmoothed"). */
    toolName?: string;
    /** Rebuild the selected group as a different tool / mode (toolline switcher).
     * Distance is Standard-only, so the panel never offers Distance + Smoothed. */
    onConvert?: (
        tool: 'distance' | 'height' | 'distance_height',
        mode: 'standard' | 'smoothed'
    ) => void;
}

type ConvertTool = 'distance' | 'height' | 'distance_height';
type ConvertMode = 'standard' | 'smoothed';

const TOOL_OPTIONS: { key: ConvertTool; label: string }[] = [
    { key: 'distance', label: 'Distance' },
    { key: 'height', label: 'Slope' },
    { key: 'distance_height', label: 'Distance & Slope' },
];
const MODE_OPTIONS: { key: ConvertMode; label: string }[] = [
    { key: 'standard', label: 'Standard' },
    { key: 'smoothed', label: 'Smoothed' },
];

/** Derive the tool label, mode and which Result fields to show from a Potree
 * measurement name. */
function describeTool(name: string): {
    tool: string;
    mode: string;
    fields: 'distance' | 'slope' | 'both';
} {
    const mode = name.includes('Smoothed') ? 'Smoothed' : 'Standard';
    if (name.includes('DistanceAndSlope')) return { tool: 'Distance & Slope', mode, fields: 'both' };
    if (name.includes('Height')) return { tool: 'Slope', mode, fields: 'slope' };
    if (name.includes('Distance')) return { tool: 'Distance', mode, fields: 'distance' };
    return { tool: 'Distance & Slope', mode, fields: 'both' };
}

/** Inches per native unit — everything (thresholds, floor, display) is in inches. */
const IN_PER_UNIT: Record<'m' | 'ft', number> = { m: 39.3701, ft: 12 };

type MetricLevel = 'good' | 'moderate' | 'poor' | 'pending';
type MetricKey =
    | 'nearbyPoints'
    | 'elevationSpread'
    | 'centroidOffset'
    | 'residualSpread'
    | 'verticalOffset'
    | 'planeTilt';

const LEVEL_RANK: Record<MetricLevel, number> = {
    pending: 0,
    good: 1,
    moderate: 2,
    poor: 3,
};

const METRIC_TOOLTIPS: Record<MetricKey, Partial<Record<MetricLevel, string>>> = {
    nearbyPoints: {
        good: 'A sufficient number of nearby points were found.',
        moderate: 'A representative point was generated from a limited number of nearby points.',
        poor: 'The representative point was generated, but very few nearby points were available.',
    },
    elevationSpread: {
        good: 'Nearby points have consistent elevation.',
        moderate: 'Moderate elevation variation was detected.',
        poor: 'Large elevation variation may reduce measurement quality.',
    },
    centroidOffset: {
        good: 'The representative point is close to the selected endpoint.',
        moderate: 'The representative point differs moderately from the selected endpoint.',
        poor: 'The representative point differs significantly from the selected endpoint.',
    },
    residualSpread: {
        good: 'Nearby points sit tightly on one surface (local slope removed).',
        moderate: 'Some scatter remains around the local surface.',
        poor: 'Nearby points do not form one clean surface — possible double layer, curb face or edge.',
    },
    verticalOffset: {
        good: 'The clicked point sits right on the local surface.',
        moderate: 'The clicked point sits somewhat above/below the local surface.',
        poor: 'The clicked point floats well off the local surface — likely a stray/flying point.',
    },
    planeTilt: {
        good: 'The local surface is flat enough to be ground or a ramp.',
        moderate: 'The local surface is fairly steep — check it is not a curb face.',
        poor: 'The local surface is near-vertical — likely a wall / curb face, not the ground.',
    },
};

/** Nearby Points: >10 green, 5–10 yellow, <5 red */
function assessNearbyPoints(n: number): MetricLevel {
    if (n > 10) return 'good';
    if (n >= 5) return 'moderate';
    return 'poor';
}

/** Elevation Spread (inches): <0.24 green, 0.24–0.60 yellow, >0.60 red */
function assessElevationSpread(value: number | null): MetricLevel {
    if (value === null) return 'pending';
    if (value < 0.24) return 'good';
    if (value <= 0.6) return 'moderate';
    return 'poor';
}

/**
 * A one-sided hemisphere pulls the median ~4r/3π off the click even for a
 * perfect drop, so a raw offset can never reach green. Grade the *excess* above
 * that geometric floor instead. The floor tracks HEMISPHERE_RADIUS_FT (× 12 → in),
 * so it auto-adjusts if the radius changes.
 */
const CENTROID_OFFSET_FLOOR_IN = (4 * (HEMISPHERE_RADIUS_FT * 12)) / (3 * Math.PI);

/** Centroid Offset (excess over floor, inches): <0.36 green, 0.36–1.20 yellow, >1.20 red */
function assessCentroidOffset(value: number | null): MetricLevel {
    if (value === null) return 'pending';
    const excess = value - CENTROID_OFFSET_FLOOR_IN;
    if (excess < 0.36) return 'good';
    if (excess <= 1.2) return 'moderate';
    return 'poor';
}

/** Residual Spread (roughness after removing local slope, inches): <0.24 green, 0.24–0.60 yellow, >0.60 red */
function assessResidualSpread(value: number | null): MetricLevel {
    if (value === null) return 'pending';
    if (value < 0.24) return 'good';
    if (value <= 0.6) return 'moderate';
    return 'poor';
}

/** Vertical Offset (click's height off the local surface, inches): <0.36 green, 0.36–1.20 yellow, >1.20 red */
function assessVerticalOffset(value: number | null): MetricLevel {
    if (value === null) return 'pending';
    if (value < 0.36) return 'good';
    if (value <= 1.2) return 'moderate';
    return 'poor';
}

/**
 * Plane tilt = tan(angle from horizontal) = |gradient| of the local surface.
 * A ramp is single-digit percent (~0.05–0.15); a curb face / wall is near-vertical.
 * <0.5 (≈27°) ground/ramp, 0.5–1.0 steep/suspicious, >1.0 (>45°) a wall.
 */
function assessPlaneTilt(value: number | null): MetricLevel {
    if (value === null) return 'pending';
    if (value < 0.5) return 'good';
    if (value <= 1.0) return 'moderate';
    return 'poor';
}

// --- Failure-cause read-out -------------------------------------------------
// We map the six checks onto the five known failure causes. The mapping is not
// 1:1: Residual Spread feeds BOTH "double surface" and "mixed pixel", so those two
// causes move together. Only "vertical face" has its own independent signal
// (Plane Tilt). This is surfaced honestly in the hover.
type CauseKey = 'flying' | 'samples' | 'doubleSurface' | 'verticalFace' | 'mixedPixel';

const CAUSE_ORDER: CauseKey[] = ['samples', 'flying', 'doubleSurface', 'verticalFace', 'mixedPixel'];

const CAUSE_LABEL: Record<CauseKey, string> = {
    flying: 'Flying point',
    samples: 'Too few points',
    doubleSurface: 'Double surface',
    verticalFace: 'Vertical face',
    mixedPixel: 'Mixed pixel',
};

// Row hover — the symptom, in plain language.
const CAUSE_DESC: Record<CauseKey, string> = {
    flying: 'The point hangs above/below the real surface, nothing around it.',
    samples: 'Too few neighbouring points to trust this endpoint (the cloud may still be loading).',
    doubleSurface: 'Two misaligned passes — the surface splits into two layers ~2–3 cm apart.',
    verticalFace: 'The neighbours land on a vertical face (curb / wall), not the ground.',
    mixedPixel: 'A mid-air ghost point where the laser spot straddled an edge.',
};

// Per-row status word.
const LEVEL_WORD: Record<MetricLevel, string> = {
    good: 'OK',
    moderate: 'Watch',
    poor: 'Risk',
    pending: '—',
};

// Endpoint header word (overall).
const HEADER_WORD: Record<MetricLevel, string> = {
    good: 'Clean',
    moderate: 'Watch',
    poor: 'Risk',
    pending: '—',
};

type CauseLevels = Record<CauseKey, MetricLevel>;

/** Worst of the given levels, ignoring 'pending'; 'pending' only if all are. */
function combineLevels(...levels: MetricLevel[]): MetricLevel {
    const present = levels.filter((l) => l !== 'pending');
    if (present.length === 0) return 'pending';
    return present.reduce(
        (worst, l) => (LEVEL_RANK[l] > LEVEL_RANK[worst] ? l : worst),
        'good' as MetricLevel
    );
}

interface MetricEntry {
    key: MetricKey;
    label: string;
    value: string;
    level: MetricLevel;
}

/** Header hover: the six raw checks (value + one-line reading) behind the causes. */
const MetricHover: React.FC<{ metrics: MetricEntry[] }> = ({ metrics }) => (
    <div className="neighbor-validation-tooltip__body">
        {metrics.map((m) => (
            <div key={m.key} className="neighbor-validation-panel__metric-hover-row">
                <span
                    className={`neighbor-validation-panel__cause-dot neighbor-validation-panel__cause-dot--${m.level}`}
                />
                <span className="neighbor-validation-panel__metric-hover-name">
                    {m.label} — {m.value}
                </span>
                <span className="neighbor-validation-panel__metric-hover-desc">
                    {METRIC_TOOLTIPS[m.key][m.level]}
                </span>
            </div>
        ))}
    </div>
);

interface CauseBasis {
    label: string;
    value: string;
    level: MetricLevel;
}

/**
 * Cause-row hover: the symptom, then the metric(s) this cause is judged from —
 * with their current reading and OK/Watch/Risk — so the user sees *why* the cause
 * got this score.
 */
const CauseRowHover: React.FC<{ desc: string; basis: CauseBasis[] }> = ({ desc, basis }) => (
    <div className="neighbor-validation-tooltip__body">
        <p>{desc}</p>
        <p className="neighbor-validation-panel__cause-basis-head">Judged from:</p>
        {basis.map((b) => (
            <div key={b.label} className="neighbor-validation-panel__metric-hover-row">
                <span
                    className={`neighbor-validation-panel__cause-dot neighbor-validation-panel__cause-dot--${b.level}`}
                />
                <span className="neighbor-validation-panel__metric-hover-name">
                    {b.label} — {b.value}
                </span>
                <span className="neighbor-validation-panel__cause-basis-word">{LEVEL_WORD[b.level]}</span>
            </div>
        ))}
    </div>
);

// Collapse toggles: framed minus (expanded) / plus (collapsed). currentColor so
// they inherit the button's colour + hover.
const IconMinusSquare: React.FC = () => (
    <svg width="15" height="15" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M8.46438 5.57447L3.53581 5.57447C3.47688 5.57447 3.42867 5.62269 3.42867 5.68162V6.32447C3.42867 6.3834 3.47688 6.43162 3.53581 6.43162L8.46438 6.43162C8.52331 6.43162 8.57152 6.3834 8.57152 6.32447V5.68162C8.57152 5.62269 8.52331 5.57447 8.46438 5.57447Z" fill="currentColor"/>
        <path d="M1.07122 11.3594L10.9284 11.3594C11.1654 11.3594 11.3569 11.1679 11.3569 10.9308L11.3569 1.07366C11.3569 0.836606 11.1654 0.645088 10.9284 0.645088L1.07122 0.645088C0.834165 0.645088 0.642646 0.836606 0.642646 1.07366L0.642646 10.9308C0.642646 11.1679 0.834165 11.3594 1.07122 11.3594ZM1.60693 1.60937L10.3926 1.60937L10.3926 10.3951L1.60693 10.3951L1.60693 1.60937Z" fill="currentColor"/>
    </svg>
);
const IconPlusSquare: React.FC = () => (
    <svg width="15" height="15" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M8.46438 5.57325L6.42867 5.57325V3.53754C6.42867 3.47861 6.38045 3.4304 6.32152 3.4304H5.67867C5.61974 3.4304 5.57152 3.47861 5.57152 3.53754V5.57325H3.53581C3.47688 5.57325 3.42867 5.62147 3.42867 5.6804V6.32325C3.42867 6.38218 3.47688 6.4304 3.53581 6.4304H5.57152L5.57152 8.46611C5.57152 8.52504 5.61974 8.57325 5.67867 8.57325H6.32152C6.38045 8.57325 6.42867 8.52504 6.42867 8.46611L6.42867 6.4304L8.46438 6.4304C8.52331 6.4304 8.57152 6.38218 8.57152 6.32325V5.6804C8.57152 5.62147 8.52331 5.57325 8.46438 5.57325Z" fill="currentColor"/>
        <path d="M1.07122 11.3594L10.9284 11.3594C11.1654 11.3594 11.3569 11.1679 11.3569 10.9308L11.3569 1.07366C11.3569 0.836606 11.1654 0.645088 10.9284 0.645088L1.07122 0.645088C0.834165 0.645088 0.642647 0.836606 0.642647 1.07366L0.642647 10.9308C0.642647 11.1679 0.834165 11.3594 1.07122 11.3594ZM1.60693 1.60937L10.3926 1.60937L10.3926 10.3951L1.60693 10.3951L1.60693 1.60937Z" fill="currentColor"/>
    </svg>
);

// Panel-level toggle: plain +/- glyphs, no square frame around them.
const IconMinus: React.FC = () => (
    <svg width="15" height="15" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M2.5 6H9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
);
const IconPlus: React.FC = () => (
    <svg width="15" height="15" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M2.5 6H9.5M6 2.5V9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
);

const PointSection: React.FC<{
    label: string;
    summary: NeighborSummary | null;
    lengthUnit: 'm' | 'ft';
    distanceUnit?: { code: string; perNativeUnit: number };
    /** Vertical/height measurement — surface checks don't apply, so ratings show "—". */
    verticalMode?: boolean;
}> = ({ label, summary, lengthUnit, distanceUnit, verticalMode }) => {
    // Per-endpoint collapse, persisted per label; defaults collapsed.
    const pointStorageKey = `measurementReview.point.${label}`;
    const [open, setOpen] = React.useState(() => readStoredBool(pointStorageKey, false));
    React.useEffect(() => writeStoredBool(pointStorageKey, open), [pointStorageKey, open]);
    // Vertical/height measurement → surface checks don't apply; auto-collapse so the
    // "—" ratings don't clutter (the user can still expand to view them).
    React.useEffect(() => {
        if (verticalMode) setOpen(false);
    }, [verticalMode]);

    if (!summary) {
        return (
            <div className="neighbor-validation-panel__section">
                <div className="neighbor-validation-panel__section-header neighbor-validation-panel__section-header--pending">
                    <span className="neighbor-validation-panel__section-label">{label}</span>
                    <span className="neighbor-validation-panel__quality">—</span>
                </div>
                <div className="neighbor-validation-panel__pending">Waiting for both points…</div>
            </div>
        );
    }

    // One conversion, native → inches, shared by grading and display. Assumes the
    // viewer's display unit is inches; the fallback forces inches too.
    const dUnit = distanceUnit ?? { code: '″', perNativeUnit: IN_PER_UNIT[lengthUnit] };
    const perIn = dUnit.perNativeUnit;
    const toIn = (native: number | null) => (native === null ? null : native * perIn);
    const fmtDisp = (native: number | null) =>
        native === null ? '—' : `${(native * perIn).toFixed(3)} ${dUnit.code}`;

    const nearbyLevel = assessNearbyPoints(summary.n);
    const spreadLevel = assessElevationSpread(toIn(summary.zSpread));
    const offsetLevel = assessCentroidOffset(toIn(summary.distance));
    const residualSpreadLevel = assessResidualSpread(toIn(summary.residualSpread));
    const verticalOffsetLevel = assessVerticalOffset(toIn(summary.verticalOffset));
    // Plane tilt is unitless (rise/run), so no inch conversion.
    const planeTiltLevel = assessPlaneTilt(summary.planeTilt);

    // The list shows the five known causes (from the metrics); the metrics
    // themselves — the raw readings behind each cause — move into the header hover.
    const causeLevels: CauseLevels = {
        flying: combineLevels(verticalOffsetLevel, offsetLevel),
        samples: nearbyLevel,
        doubleSurface: residualSpreadLevel,
        verticalFace: planeTiltLevel,
        mixedPixel: combineLevels(residualSpreadLevel, verticalOffsetLevel),
    };
    const overallLevel = combineLevels(...CAUSE_ORDER.map((k) => causeLevels[k]));

    const metrics: MetricEntry[] = [
        { key: 'nearbyPoints', label: 'Nearby Points', value: String(summary.n), level: nearbyLevel },
        { key: 'elevationSpread', label: 'Elevation Spread', value: fmtDisp(summary.zSpread), level: spreadLevel },
        { key: 'residualSpread', label: 'Residual Spread', value: fmtDisp(summary.residualSpread), level: residualSpreadLevel },
        { key: 'centroidOffset', label: 'Centroid Offset', value: fmtDisp(summary.distance), level: offsetLevel },
        { key: 'verticalOffset', label: 'Vertical Offset', value: fmtDisp(summary.verticalOffset), level: verticalOffsetLevel },
        { key: 'planeTilt', label: 'Plane Tilt', value: summary.planeTilt === null ? '—' : summary.planeTilt.toFixed(2), level: planeTiltLevel },
    ];
    const metricByKey: Partial<Record<MetricKey, MetricEntry>> = {};
    metrics.forEach((m) => (metricByKey[m.key] = m));

    // Which check(s) each cause is judged from — shown in the row hover so the
    // user sees why the cause got its score.
    const causeBasis: Record<CauseKey, CauseBasis[]> = {
        samples: [metricByKey.nearbyPoints as CauseBasis],
        flying: [metricByKey.verticalOffset as CauseBasis, metricByKey.centroidOffset as CauseBasis],
        doubleSurface: [metricByKey.residualSpread as CauseBasis],
        verticalFace: [metricByKey.planeTilt as CauseBasis],
        mixedPixel: [metricByKey.residualSpread as CauseBasis, metricByKey.verticalOffset as CauseBasis],
    };

    // Two special states:
    //  • noPoints (n === 0): nothing was found here → can't compute at all; guide the
    //    user to reposition instead of showing a scary "Risk".
    //  • blanked (vertical measurement): surface checks don't apply → show ratings as "—".
    const noPoints = summary.n === 0;
    const blanked = !noPoints && !!verticalMode;

    const headerLevel: MetricLevel = noPoints ? 'moderate' : blanked ? 'pending' : overallLevel;
    const headerText = noPoints ? 'Move point' : blanked ? '—' : HEADER_WORD[overallLevel];
    const showMetricHover = !noPoints && !blanked;

    const badge = (
        <div className="neighbor-validation-panel__status-dot-container">
            <span className="neighbor-validation-panel__status-dot" />
            <span className="neighbor-validation-panel__quality">{headerText}</span>
        </div>
    );

    return (
        <div className="neighbor-validation-panel__section">
            <div className={`neighbor-validation-panel__section-header neighbor-validation-panel__section-header--${headerLevel}`}>
                <span className="neighbor-validation-panel__section-left">
                    <button
                        type="button"
                        className="neighbor-validation-panel__point-toggle"
                        onClick={() => setOpen((o) => !o)}
                        aria-expanded={open}
                        aria-label={`${open ? 'Collapse' : 'Expand'} ${label} causes`}
                    >
                        {open ? <IconMinusSquare /> : <IconPlusSquare />}
                    </button>
                    <span className="neighbor-validation-panel__section-label">{label}</span>
                </span>
                {showMetricHover ? (
                    <Tooltip
                        title={<MetricHover metrics={metrics} />}
                        placement="right"
                        overlayClassName="neighbor-validation-tooltip"
                    >
                        {badge}
                    </Tooltip>
                ) : (
                    badge
                )}
            </div>
            {open &&
                (noPoints ? (
                    <div className="neighbor-validation-panel__pending neighbor-validation-panel__movepoint-hint">
                        No points here — move this point onto denser cloud.
                    </div>
                ) : (
                    <div className="neighbor-validation-panel__cause-list">
                        {CAUSE_ORDER.map((key) => {
                            const level = blanked ? 'pending' : causeLevels[key];
                            const row = (
                                <div className={`neighbor-validation-panel__cause-item neighbor-validation-panel__cause-item--${level}`}>
                                    <span className={`neighbor-validation-panel__cause-item-dot neighbor-validation-panel__cause-item-dot--${level}`} />
                                    <span className="neighbor-validation-panel__cause-item-name">{CAUSE_LABEL[key]}</span>
                                    <span className="neighbor-validation-panel__cause-item-word">{LEVEL_WORD[level]}</span>
                                </div>
                            );
                            // Blanked (vertical) rows carry no rating, so no basis hover.
                            return blanked ? (
                                <React.Fragment key={key}>{row}</React.Fragment>
                            ) : (
                                <Tooltip
                                    key={key}
                                    title={<CauseRowHover desc={CAUSE_DESC[key]} basis={causeBasis[key]} />}
                                    placement="right"
                                    overlayClassName="neighbor-validation-tooltip"
                                >
                                    {row}
                                </Tooltip>
                            );
                        })}
                    </div>
                ))}
        </div>
    );
};

const TITLE_TOOLTIP = (
    <div className="neighbor-validation-tooltip__body">
        <p><b>Measurement Review</b> has two parts:</p>

        <hr className="neighbor-validation-tooltip__divider" />
        <div className="neighbor-validation-tooltip__head">Result</div>
        <p>The measured slope / distance.</p>
        <p>
            In <b>Smoothed</b> mode it comes from a representative point (<b>P′</b>) —
            the median of the nearby cloud points — instead of your single click.
        </p>

        <hr className="neighbor-validation-tooltip__divider" />
        <div className="neighbor-validation-tooltip__head">Point Quality</div>
        <p>Rates how reliable each endpoint is.</p>
    </div>
);

// Overview of the six per-endpoint checks: how each is derived, what it flags,
// and what the Extractor should do about it.
const POINT_QUALITY_TOOLTIP = (
    <div className="neighbor-validation-tooltip__body">
        <p><b>Point Quality</b> rates how far to trust each end of the measurement.</p>
        <p className="neighbor-validation-tooltip__sub">
            Each end — <b>Start Point</b> and <b>End Point</b> — is read from the cloud
            points around your click.
        </p>

        <hr className="neighbor-validation-tooltip__divider" />
        <div className="neighbor-validation-tooltip__head">The five causes</div>
        <p className="neighbor-validation-tooltip__sub">
            Listed under each endpoint, each rated OK / Watch / Risk. Hover a cause to
            see its symptom and which check(s) it's judged from (value + level).
        </p>
        <div className="neighbor-validation-tooltip__causes">
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Too few points</span>
                <span className="neighbor-validation-tooltip__cause-desc">Not enough nearby points to trust this endpoint (may still be loading).</span>
            </div>
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Flying point</span>
                <span className="neighbor-validation-tooltip__cause-desc">The click sits off the surface — a stray / floating point.</span>
            </div>
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Double surface</span>
                <span className="neighbor-validation-tooltip__cause-desc">Two layers ~2–3 cm apart (misaligned passes).</span>
            </div>
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Vertical face</span>
                <span className="neighbor-validation-tooltip__cause-desc">A wall / curb face, not the ground.</span>
            </div>
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Mixed pixel</span>
                <span className="neighbor-validation-tooltip__cause-desc">A mid-air ghost point where the laser spot straddled an edge.</span>
            </div>
        </div>

        <hr className="neighbor-validation-tooltip__divider" />
        <div className="neighbor-validation-tooltip__head">The badge — right of Start / End Point</div>
        <p>This badge is the endpoint's overall verdict — set by its worst check:</p>
        <div className="neighbor-validation-tooltip__verdict">
            <span className="neighbor-validation-tooltip__verdict-item">
                <span className="neighbor-validation-tooltip__vdot neighbor-validation-tooltip__vdot--good" />Clean
            </span>
            <span className="neighbor-validation-tooltip__verdict-item">
                <span className="neighbor-validation-tooltip__vdot neighbor-validation-tooltip__vdot--moderate" />Watch
            </span>
            <span className="neighbor-validation-tooltip__verdict-item">
                <span className="neighbor-validation-tooltip__vdot neighbor-validation-tooltip__vdot--poor" />Risk
            </span>
        </div>
        <p className="neighbor-validation-tooltip__sub"><b>Hover the badge</b> to see all six checks with their current readings:</p>
        <div className="neighbor-validation-tooltip__causes">
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Nearby Points</span>
                <span className="neighbor-validation-tooltip__cause-desc">How many cloud points are near the click — the base every other check rests on.</span>
            </div>
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Elevation Spread</span>
                <span className="neighbor-validation-tooltip__cause-desc">How much the nearby heights vary (also reads high on a clean but steep slope).</span>
            </div>
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Residual Spread</span>
                <span className="neighbor-validation-tooltip__cause-desc">Roughness once the slope is removed — is it one clean surface, or a mess?</span>
            </div>
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Centroid Offset</span>
                <span className="neighbor-validation-tooltip__cause-desc">How far the click landed from the middle of the nearby points.</span>
            </div>
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Vertical Offset</span>
                <span className="neighbor-validation-tooltip__cause-desc">How far the click floats above or below the surface.</span>
            </div>
            <div className="neighbor-validation-tooltip__cause">
                <span className="neighbor-validation-tooltip__cause-name">Plane Tilt</span>
                <span className="neighbor-validation-tooltip__cause-desc">How steep the surface is (rise/run) — near-vertical means a wall / curb face.</span>
            </div>
        </div>
    </div>
);

// Persist the panel's own collapse toggle across sessions (default: open).
// Point Quality no longer collapses independently — it always shows once the
// panel itself is open.
const LS_PANEL_OPEN = 'measurementReview.panelOpen';

function readStoredBool(key: string, fallback: boolean): boolean {
    try {
        const v = window.localStorage.getItem(key);
        return v === null ? fallback : v === '1';
    } catch {
        return fallback;
    }
}

function writeStoredBool(key: string, value: boolean): void {
    try {
        window.localStorage.setItem(key, value ? '1' : '0');
    } catch {
        // localStorage may be unavailable (private mode / blocked) — ignore.
    }
}

export const NeighborValidationPanel: React.FC<Props> = ({
    visible,
    start,
    end,
    smoothedMeasure = null,
    lengthUnit = 'ft',
    distanceUnit,
    mode = 'smoothed',
    toolName = '',
    onConvert,
}) => {
    const { tool, mode: toolMode, fields: toolFields } = describeTool(toolName);
    const currentToolKey: ConvertTool =
        toolFields === 'distance' ? 'distance' : toolFields === 'slope' ? 'height' : 'distance_height';
    const currentModeKey: ConvertMode = toolMode === 'Smoothed' ? 'smoothed' : 'standard';
    // The whole panel collapses to just its title bar.
    const [panelOpen, setPanelOpen] = React.useState(() => readStoredBool(LS_PANEL_OPEN, true));
    React.useEffect(() => writeStoredBool(LS_PANEL_OPEN, panelOpen), [panelOpen]);
    // Toolline tool/mode switcher dropdown ('tool' | 'mode' | null). The menu is
    // position:fixed with a viewport-space anchor so it escapes the panel's own
    // overflow:auto (which would otherwise clip it and add a scrollbar).
    const [openMenu, setOpenMenu] = React.useState<null | 'tool' | 'mode'>(null);
    const [menuPos, setMenuPos] = React.useState<{ top: number; left: number } | null>(null);
    const menuRef = React.useRef<HTMLDivElement | null>(null);
    const toggleMenu = (which: 'tool' | 'mode', e: React.MouseEvent<HTMLButtonElement>) => {
        if (!onConvert) return;
        if (openMenu === which) {
            setOpenMenu(null);
            return;
        }
        const r = e.currentTarget.getBoundingClientRect();
        setMenuPos({ top: r.bottom + 4, left: r.left });
        setOpenMenu(which);
    };
    React.useEffect(() => {
        if (!openMenu) return;
        const close = () => setOpenMenu(null);
        const onDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpenMenu(null);
            }
        };
        document.addEventListener('mousedown', onDown);
        // A fixed menu can't follow the panel if it scrolls — just close it.
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [openMenu]);
    // The two "i" info explainers are too long for a hover tooltip — they open as
    // a modal instead, dismissed by the X / overlay / Esc.
    const [titleInfoOpen, setTitleInfoOpen] = React.useState(false);
    const [qualityInfoOpen, setQualityInfoOpen] = React.useState(false);
    if (!visible) return null;

    // Measured distance is shown in inches; use the viewer's display unit when
    // provided, otherwise a fixed native → inches conversion.
    const dUnit = distanceUnit ?? { code: '″', perNativeUnit: IN_PER_UNIT[lengthUnit] };
    const fmtDist = (native: number) => `${(native * dUnit.perNativeUnit).toFixed(2)} ${dUnit.code}`;

    // Standard = raw clicked points (computed here). Smoothed comes from the
    // slope-corrected P′ that Sidebar computes, so this row matches the on-line
    // labels exactly (§11 keeps P′ horizontally at the click).
    const standard =
        start && end
            ? computeDistanceAndSlope(start.marker.position, end.marker.position)
            : null;
    const smoothed = smoothedMeasure;

    // Line geometry → the vertical-mode hint next to RESULT, and gates Point Quality.
    // Uses the click positions, same basis as isNearVertical / correctPPrimeForSlope.
    let lineAngleDeg: number | null = null;
    let verticalMode = false;
    if (start?.marker?.position && end?.marker?.position) {
        const p0 = start.marker.position;
        const p1 = end.marker.position;
        const run = Math.hypot(p1.x - p0.x, p1.y - p0.y);
        const rise = Math.abs(p1.z - p0.z);
        lineAngleDeg = (Math.atan2(rise, run) * 180) / Math.PI;
        verticalMode = isNearVertical(p0, p1);
    }
    const signed = (v: number, decimals = 2) =>
        `${v >= 0 ? '+' : '-'}${Math.abs(v).toFixed(decimals)}`;

    const showSlope = toolFields !== 'distance';
    const showDistance = toolFields !== 'slope';
    // Vertical → slope is meaningless: hide the Standard slope for any tool that
    // measures slope. Distance & Slope keeps its Standard distance; the Slope-only
    // tool is then left with nothing (voided).
    const hideStandardSlope = verticalMode && showSlope;

    // Click a value to copy its number (like clicking an on-line label). The toast
    // names what was copied ("Slope copied" / "Distance copied"), matching the
    // auto-copy and Potree's own label-copy.
    const copyNum = (n: number, kind: 'Slope' | 'Distance') => {
        const text = n.toFixed(2);
        navigator.clipboard?.writeText(text);
        const w = window as unknown as { showCopySuccess?: (m: string) => void };
        if (w.showCopySuccess) w.showCopySuccess(`${kind} copied: ${text}`);
    };
    const copyCell = (display: string, num: number | null, kind: 'Slope' | 'Distance') =>
        num === null ? (
            <span>{display}</span>
        ) : (
            <span
                className="neighbor-validation-panel__copyable"
                onClick={() => copyNum(num, kind)}
                title="Click to copy"
            >
                {display}
            </span>
        );
    const perIn = dUnit.perNativeUnit;

    return (
        <div className="neighbor-validation-panel">
            <div
                className="neighbor-validation-panel__title neighbor-validation-panel__title--toggle"
                onClick={() => setPanelOpen((open) => !open)}
                role="button"
                aria-expanded={panelOpen}
            >
                <span>Measurement Review
                    <span
                        className="neighbor-validation-panel__title-info"
                        aria-label="About measurement review"
                        role="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setTitleInfoOpen(true);
                        }}
                    >
                        i
                    </span>
                </span>
                
                {panelOpen ? <IconMinus /> : <IconPlus />}

            </div>
            
            {panelOpen && (
                <div className='neighbor-validation-panel__container'>
                    {toolName && (
                        <div className="neighbor-validation-panel__toolline" ref={menuRef}>
                            <div className="neighbor-validation-panel__toolpick">
                                <button
                                    type="button"
                                    className={`neighbor-validation-panel__toolline-btn neighbor-validation-panel__toolline-mode neighbor-validation-panel__toolline-mode--${toolMode.toLowerCase()}`}
                                    onClick={(e) => toggleMenu('mode', e)}
                                    disabled={!onConvert}
                                    aria-haspopup="listbox"
                                    aria-expanded={openMenu === 'mode'}
                                >
                                    {toolMode}
                                </button>
                                {openMenu === 'mode' && (
                                    <div
                                        style={
                                            menuPos
                                                ? { top: menuPos.top, left: menuPos.left }
                                                : undefined
                                        }
                                        className="neighbor-validation-panel__toolmenu"
                                        role="listbox"
                                    >
                                        {MODE_OPTIONS.map((opt) => {
                                            // Distance has no Smoothed mode.
                                            const disabled =
                                                currentToolKey === 'distance' &&
                                                opt.key === 'smoothed';
                                            return (
                                                <button
                                                    key={opt.key}
                                                    type="button"
                                                    // Not native-disabled: a disabled button
                                                    // swallows the title tooltip in Chrome, and we
                                                    // want the "why" to show on hover.
                                                    aria-disabled={disabled}
                                                    title={
                                                        disabled
                                                            ? 'Distance supports Standard only'
                                                            : undefined
                                                    }
                                                    className={`neighbor-validation-panel__toolmenu-item${
                                                        opt.key === currentModeKey ? ' is-active' : ''
                                                    }${disabled ? ' is-disabled' : ''}`}
                                                    onClick={() => {
                                                        if (disabled) return;
                                                        setOpenMenu(null);
                                                        if (opt.key === currentModeKey) return;
                                                        onConvert?.(currentToolKey, opt.key);
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <div className="neighbor-validation-panel__toolpick">
                                <button
                                    type="button"
                                    className="neighbor-validation-panel__toolline-btn neighbor-validation-panel__toolline-tool"
                                    onClick={(e) => toggleMenu('tool', e)}
                                    disabled={!onConvert}
                                    aria-haspopup="listbox"
                                    aria-expanded={openMenu === 'tool'}
                                >
                                    {tool}
                                </button>
                                {openMenu === 'tool' && (
                                    <div
                                        className="neighbor-validation-panel__toolmenu"
                                        role="listbox"
                                        style={
                                            menuPos
                                                ? { top: menuPos.top, left: menuPos.left }
                                                : undefined
                                        }
                                    >
                                        {TOOL_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                className={`neighbor-validation-panel__toolmenu-item${
                                                    opt.key === currentToolKey ? ' is-active' : ''
                                                }`}
                                                onClick={() => {
                                                    setOpenMenu(null);
                                                    if (opt.key === currentToolKey) return;
                                                    // Distance is Standard-only; other tools keep
                                                    // the current mode.
                                                    const nextMode: ConvertMode =
                                                        opt.key === 'distance'
                                                            ? 'standard'
                                                            : currentModeKey;
                                                    onConvert?.(opt.key, nextMode);
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {standard && (
                        <div className="neighbor-validation-panel__subhead">
                            <span className="neighbor-validation-panel__subhead-text">Result</span>
                            {verticalMode && lineAngleDeg !== null && (
                                <span className="neighbor-validation-panel__vhint neighbor-validation-panel__vhint--on">
                                    Vertical · {Math.round(lineAngleDeg)}°
                                </span>
                            )}
                        </div>
                    )}
                    {/* Smoothed: Standard vs Smoothed vs Δ comparison. Only the field(s) the
                tool measures carry values; the other column is "—" (Slope tool → no
                distance, Distance tool → no slope). */}
                    {standard && mode === 'smoothed' && (
                        <div className="neighbor-validation-panel__compare">
                            <div className="neighbor-validation-panel__compare-row neighbor-validation-panel__compare--head">
                                <span />
                                <span>Slope</span>
                                <span>Distance</span>
                            </div>
                            <div className="neighbor-validation-panel__compare-row">
                                <span className="neighbor-validation-panel__compare-label">Standard</span>
                                {copyCell(showSlope && !hideStandardSlope ? `${standard.slopePercent.toFixed(2)}%` : '—', showSlope && !hideStandardSlope ? standard.slopePercent : null, 'Slope')}
                                {copyCell(showDistance ? fmtDist(standard.distance) : '—', showDistance ? standard.distance * perIn : null, 'Distance')}
                            </div>
                            <div className="neighbor-validation-panel__compare-row">
                                <span className="neighbor-validation-panel__compare-label">Smoothed</span>
                                {copyCell(smoothed && showSlope ? `${smoothed.slopePercent.toFixed(2)}%` : '—', smoothed && showSlope ? smoothed.slopePercent : null, 'Slope')}
                                {/* Smoothed never changes the distance — it stays Standard, so no Smoothed/Δ distance. */}
                                {copyCell('—', null, 'Distance')}
                            </div>
                            <div className="neighbor-validation-panel__compare-row neighbor-validation-panel__compare--delta">
                                <span className="neighbor-validation-panel__compare-label">Δ</span>
                                <span>{smoothed && showSlope ? `${signed(smoothed.slopePercent - standard.slopePercent)} pp` : '—'}</span>
                                <span>—</span>
                            </div>
                        </div>
                    )}
                    {/* Standard: same table as Smoothed but only the Standard row. The
                field the tool doesn't measure shows "—" (Distance tool → no slope,
                Slope tool → no distance). */}
                    {standard && mode === 'standard' && (
                        <div className="neighbor-validation-panel__compare">
                            <div className="neighbor-validation-panel__compare-row neighbor-validation-panel__compare--head">
                                <span />
                                <span>Slope</span>
                                <span>Distance</span>
                            </div>
                            <div className="neighbor-validation-panel__compare-row">
                                <span className="neighbor-validation-panel__compare-label">Standard</span>
                                {/* Vertical → slope is meaningless (run→0 makes % explode),
                                    so void it to "—" like the Smoothed table and on-line label. */}
                                {copyCell(showSlope && !hideStandardSlope ? `${standard.slopePercent.toFixed(2)}%` : '—', showSlope && !hideStandardSlope ? standard.slopePercent : null, 'Slope')}
                                {copyCell(showDistance ? fmtDist(standard.distance) : '—', showDistance ? standard.distance * perIn : null, 'Distance')}
                            </div>
                        </div>
                    )}
                    {mode === 'smoothed' && !verticalMode && (
                        <>
                            <div className="neighbor-validation-panel__subhead">
                                <span className="neighbor-validation-panel__subhead-text">Point Quality</span>
                                <span
                                    className="neighbor-validation-panel__title-info"
                                    aria-label="About the point quality metrics"
                                    role="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setQualityInfoOpen(true);
                                    }}
                                >
                                    i
                                </span>
                            </div>
                            <div className="neighbor-validation-panel__quality-body">
                                {start && end ? (
                                    // Vertical/height measurement (e.g. a sign or pole): surface
                                    // checks don't apply, so both endpoints show their ratings as "—".
                                    <>
                                        <PointSection label="Start Point" summary={start} lengthUnit={lengthUnit} distanceUnit={distanceUnit} verticalMode={verticalMode} />
                                        <PointSection label="End Point" summary={end} lengthUnit={lengthUnit} distanceUnit={distanceUnit} verticalMode={verticalMode} />
                                    </>
                                ) : (
                                    <div className="neighbor-validation-panel__pending">Waiting for both points…</div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            <Modal
                title="Measurement Review"
                visible={titleInfoOpen}
                onCancel={() => setTitleInfoOpen(false)}
                footer={null}
                className="neighbor-validation-modal"
                wrapClassName="neighbor-validation-modal-wrap"
            >
                {TITLE_TOOLTIP}
            </Modal>
            <Modal
                title="Point Quality"
                visible={qualityInfoOpen}
                onCancel={() => setQualityInfoOpen(false)}
                footer={null}
                className="neighbor-validation-modal"
                wrapClassName="neighbor-validation-modal-wrap"
            >
                {POINT_QUALITY_TOOLTIP}
            </Modal>
        </div>
    );
};
